import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function FractionCalculator() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Math", url: "https://calclab.app/math" },
      { name: "Fraction Calculator", url: "https://calclab.app/calculator/fraction" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Fraction Calculator",
      "Add, subtract, multiply, and divide fractions with automatic simplification. Free online calculator.",
      "https://calclab.app/calculator/fraction",
      lang
    )
  ]);
  const [num1, setNum1] = useState("");
  const [den1, setDen1] = useState("");
  const [num2, setNum2] = useState("");
  const [den2, setDen2] = useState("");
  const [operation, setOperation] = useState("+");
  const [result, setResult] = useState<{ numerator: number; denominator: number } | null>(null);

  const gcd = (a: number, b: number): number => {
    return b === 0 ? Math.abs(a) : gcd(b, a % b);
  };

  const simplify = (num: number, den: number) => {
    const divisor = gcd(num, den);
    return { numerator: num / divisor, denominator: den / divisor };
  };

  const calculate = () => {
    const n1 = parseInt(num1);
    const d1 = parseInt(den1);
    const n2 = parseInt(num2);
    const d2 = parseInt(den2);

    if (isNaN(n1) || isNaN(d1) || isNaN(n2) || isNaN(d2) || d1 === 0 || d2 === 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid numbers. Denominators cannot be zero.",
        variant: "destructive",
      });
      return;
    }

    let resNum: number, resDen: number;

    switch (operation) {
      case "+":
        resNum = n1 * d2 + n2 * d1;
        resDen = d1 * d2;
        break;
      case "-":
        resNum = n1 * d2 - n2 * d1;
        resDen = d1 * d2;
        break;
      case "*":
        resNum = n1 * n2;
        resDen = d1 * d2;
        break;
      case "/":
        if (n2 === 0) {
          toast({
            title: "Error",
            description: "Cannot divide by zero.",
            variant: "destructive",
          });
          return;
        }
        resNum = n1 * d2;
        resDen = d1 * n2;
        break;
      default:
        return;
    }

    const simplified = simplify(resNum, resDen);
    setResult(simplified);

    toast({
      title: "Calculation Complete",
      description: `Result: ${simplified.numerator}/${simplified.denominator}`,
    });
  };

  const reset = () => {
    setNum1("");
    setDen1("");
    setNum2("");
    setDen2("");
    setOperation("+");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Fraction Calculator" 
      calculatorPath="/calculator/fraction"
      category="math"
      subcategory="Basic Math"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Fraction Calculator</CardTitle>
            <CardDescription>
              Add, subtract, multiply, and divide fractions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="num1">Numerator 1</Label>
                  <Input
                    id="num1"
                    type="number"
                    value={num1}
                    onChange={(e) => setNum1(e.target.value)}
                    placeholder="Top number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="den1">Denominator 1</Label>
                  <Input
                    id="den1"
                    type="number"
                    value={den1}
                    onChange={(e) => setDen1(e.target.value)}
                    placeholder="Bottom number"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="operation">Operation</Label>
                <Select value={operation} onValueChange={setOperation}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="+">Addition (+)</SelectItem>
                    <SelectItem value="-">Subtraction (−)</SelectItem>
                    <SelectItem value="*">Multiplication (×)</SelectItem>
                    <SelectItem value="/">Division (÷)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="num2">Numerator 2</Label>
                  <Input
                    id="num2"
                    type="number"
                    value={num2}
                    onChange={(e) => setNum2(e.target.value)}
                    placeholder="Top number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="den2">Denominator 2</Label>
                  <Input
                    id="den2"
                    type="number"
                    value={den2}
                    onChange={(e) => setDen2(e.target.value)}
                    placeholder="Bottom number"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result && (
              <>
                <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Result (Simplified)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center">
                    <div className="text-5xl font-bold">
                      {result.numerator}
                    </div>
                    <div className="border-t-4 border-primary my-2"></div>
                    <div className="text-5xl font-bold">
                      {result.denominator}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <CalculatorSuperCalcCTA config={calculatorCTAs["fraction"]} trigger={result?.numerator} />
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
